import React from "react";
import { Box, Stack } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";

import { useAppDispatch } from "../redux/store";
import {
  createCategory,
  updateCategory,
} from "../redux/actions/categoryActions";
import { cleanFormData } from "../helpers/formHelpers";

import { CategoryRequestDto } from "../shared/dtos/category/CategoryRequestDto";
import { CategoryResponseDto } from "../shared/dtos/category/CategoryResponseDto";
import { categoryActions } from "../redux/slices/category";

interface CreateOrUpdateCategoryFormProps {
  category?: CategoryResponseDto | null;
  isEditing?: boolean;
  loading: boolean;
  error: string | null;
}

const categorySchema = Yup.object().shape({
  name: Yup.string().required("Category name is required"),
  description: Yup.string().nullable(),
});

const CreateOrUpdateCategoryForm: React.FC<CreateOrUpdateCategoryFormProps> = ({
  category = null,
  isEditing = false,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  const initialValues = {
    name: category?.name || "",
    description: category?.description || "",
  };

  return (
    <Box sx={{ width: "100%", textAlign: "center" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={
          isEditing
            ? () => dispatch(categoryActions.setErrorUpdate(null))
            : () => dispatch(categoryActions.setErrorCreate(null))
        }
      />
      <Formik
        initialValues={initialValues}
        validationSchema={categorySchema}
        onSubmit={(values) => {
          const cleanedValues = cleanFormData(values) as CategoryRequestDto;

          if (isEditing && category) {
            dispatch(
              updateCategory({ id: category.id, values: cleanedValues }),
            );
          } else {
            dispatch(createCategory(cleanedValues));
          }
        }}
        enableReinitialize
      >
        {({ dirty, isValid }: FormikProps<typeof initialValues>) => (
          <Form>
            <Stack spacing={2}>
              <FormTextField
                name="name"
                label="Category Name"
                placeholder="e.g., Beverages"
              />
              <FormTextField
                name="description"
                label="Description"
                placeholder="e.g., All soft drinks, juices, and bottled water."
                multiline
                rows={3}
              />

              <SubmitButton
                title={isEditing ? "Save Changes" : "Create Category"}
                loading={loading}
                disabled={!dirty || !isValid || loading}
                startIcon={
                  isEditing ? <SaveOutlinedIcon /> : <AddCircleOutlineIcon />
                }
                sx={{ mt: 1 }}
              />
            </Stack>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default CreateOrUpdateCategoryForm;
